import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import { elRed } from 'src/styles/colors';
import { ASPECT_PERCENT } from 'src/config/imageConfig';
import Spinner from 'src/components/shared/Spinner';

const Box = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: ${props => props.ratioPercent || ASPECT_PERCENT}%;
  overflow: hidden;
  border-radius: ${props => props.radius || '5px'};
`;

const Image = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Loading = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.4);
`;

const AssetImage = ({ src, alt, onError, ratioPercent, radius, showLoader = true }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    setErrored(false);
    const image = imageRef.current;
    setLoaded(Boolean(image && image.complete && image.naturalWidth > 0));
  }, [src]);

  const handleError = event => {
    setErrored(true);
    setLoaded(true);
    if (onError) onError(event);
  };

  return (
    <Box ratioPercent={ratioPercent} radius={radius}>
      {src && !errored ? (
        <>
          <Image
            ref={imageRef}
            src={src}
            alt={alt || ''}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={handleError}
          />
          {showLoader && !loaded && (
            <Loading data-testid="image-loading">
              <Spinner />
            </Loading>
          )}
        </>
      ) : (
        <Placeholder>
          <FontAwesomeIcon icon={faImage} size="2xl" style={{ color: elRed }} />
        </Placeholder>
      )}
    </Box>
  );
};

export default AssetImage;
